import { Ban } from "lucide-react";
import { Button } from "@openup/shared/primitives/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@openup/shared/primitives/ui/table";
import { Badge } from "@openup/shared/primitives/ui/badge";
import { SpaceSession } from "@src/models/spaces";
import { formatSessionDateTime, formatSessionTime } from "@src/utils/format";

const expertsLabel = (session: SpaceSession): string => {
  const hosts = session.experts.filter((e) => e.role === "host");
  const others = session.experts.filter((e) => e.role !== "host");
  const parts = [
    ...hosts.map((h) => `${h.fullName} (host)`),
    ...others.map((p) => p.fullName),
  ];
  return parts.join(", ") || "—";
};

interface SessionsTableProps {
  sessions: SpaceSession[];
  /** Present only when the viewer may cancel sessions (upcoming, scheduled). */
  onCancelSession?: (session: SpaceSession) => void;
}

const SessionsTable = ({ sessions, onCancelSession }: SessionsTableProps) => (
  <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Session</TableHead>
          <TableHead>When</TableHead>
          <TableHead>Timezone</TableHead>
          <TableHead>Capacity</TableHead>
          <TableHead>Experts</TableHead>
          <TableHead>Status</TableHead>
          {onCancelSession && <TableHead />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sessions.map((session) => (
          <TableRow key={session.id}>
            <TableCell className="font-medium text-gray-800">
              {session.title}
            </TableCell>
            <TableCell>
              {formatSessionDateTime(session.startsAt)}–
              {formatSessionTime(session.endsAt)}
            </TableCell>
            <TableCell>{session.timeZone}</TableCell>
            <TableCell>{session.capacity ?? "Uncapped"}</TableCell>
            {/* Expert rosters are the one long field — wrap instead of forcing the table to scroll. */}
            <TableCell className="whitespace-normal">
              {expertsLabel(session)}
            </TableCell>
            <TableCell>
              <Badge
                variant={session.status === "scheduled" ? "success" : "light"}
              >
                {session.status}
              </Badge>
            </TableCell>
            {onCancelSession && (
              <TableCell>
                {session.status === "scheduled" && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Cancel session ${session.title}`}
                    onClick={() => onCancelSession(session)}
                  >
                    <Ban className="mr-1 h-4 w-4" aria-hidden />
                    Cancel
                  </Button>
                )}
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export default SessionsTable;
